// pages/PhoneSystemDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Building2,
  User,
  MoreVertical,
  FileText,
  Activity,
  Save,
  X as CloseIcon,
  Loader,
  PhoneCall,
  Hash,
  FileEdit,
  Calendar,
} from "lucide-react";
import apiService from "../../services/api";
import EditPhoneSystemModal from "../../components/EditPhoneSystemModal";
import { useTheme } from "../../contexts/ThemeContext";

const PhoneSystemDetail = () => {
  const { accountId, phoneSystemId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [phoneSystem, setPhoneSystem] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  useEffect(() => {
    fetchPhoneSystem();
    fetchAccount();
  }, [phoneSystemId, accountId]);

  const fetchPhoneSystem = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccountPhoneSystems(accountId);
      const system = response.data.find((ps) => ps.id === phoneSystemId);

      if (!system) {
        navigate(`/accounts/${accountId}`);
        return;
      }

      setPhoneSystem(system);
    } catch (error) {
      console.error("Error fetching phone system:", error);
      navigate(`/accounts/${accountId}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccount = async () => {
    try {
      const response = await apiService.getAccount(accountId);
      setAccount(response.data);
    } catch (error) {
      console.error("Error fetching account:", error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this phone system?")) {
      try {
        await apiService.deletePhoneSystem(accountId, phoneSystemId);
        navigate(`/accounts/${accountId}`);
      } catch (error) {
        console.error("Error deleting phone system:", error);
        alert("Failed to delete phone system");
      }
    }
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);

      const updateData = {
        systemType: phoneSystem.systemType,
        licenses: phoneSystem.licenses,
        notes: phoneSystem.notes,
        [field]: editingValue,
      };

      await apiService.updatePhoneSystem(accountId, phoneSystemId, updateData);
      await fetchPhoneSystem();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none resize-none`}
                  rows={3}
                  autoFocus
                />
              ) : type === "number" ? (
                <input
                  type="number"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              ) : (
                <input
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
              >
                <CloseIcon
                  size={16}
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                />
              </button>
            </div>
          ) : (
            <>
              <p
                className={`${isDarkMode ? "text-white" : "text-gray-900"} flex-1`}
              >
                {value || "—"}
              </p>
              <button
                onClick={() => startEditing(field, value)}
                className={`opacity-0 group-hover:opacity-100 p-1 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded transition-all`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <Loader
          className={isDarkMode ? "text-white" : "text-gray-900"}
          size={32}
        />
      </div>
    );
  }

  if (!phoneSystem) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Phone system not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(`/accounts/${accountId}`)}
              className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors`}
            >
              <ArrowLeft size={20} />
              <span>Back to Account</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
              >
                <Edit size={16} />
                <span className="hidden sm:inline">Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
                >
                  <MoreVertical
                    size={20}
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-xl z-50`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-700"} flex items-center gap-2`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} my-1`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} flex items-center gap-2 text-red-400`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Phone System Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 flex items-center justify-center text-white flex-shrink-0">
                  <PhoneCall size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {phoneSystem.systemType}
                  </h1>
                  {phoneSystem.licenses && (
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {phoneSystem.licenses} licenses
                    </div>
                  )}
                </div>

                <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-blue-500/10 text-blue-400 border-blue-500/20">
                  Phone System
                </span>
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {account && (
                  <button
                    onClick={() => navigate(`/accounts/${account.id}`)}
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <Building2 size={16} />
                    <span>{account.name}</span>
                  </button>
                )}
                {phoneSystem.licenses && (
                  <div className="flex items-center gap-2">
                    <Hash size={16} />
                    <span>{phoneSystem.licenses} licenses</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border`}
            >
              <div
                className={`flex border-b overflow-x-auto ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[{ id: "details", label: "Details", icon: FileText }].map(
                  (tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                          activeTab === tab.id
                            ? isDarkMode
                              ? "text-blue-400 border-b-2 border-blue-400"
                              : "text-blue-600 border-b-2 border-blue-600"
                            : isDarkMode
                              ? "text-gray-400 hover:text-white"
                              : "text-gray-500 hover:text-gray-900"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon size={16} />
                          {tab.label}
                        </div>
                      </button>
                    );
                  },
                )}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Phone System Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <PhoneCall size={18} />
                        Phone System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="System Type"
                          field="systemType"
                          value={phoneSystem.systemType}
                          options={[
                            { value: "NEC on Prem", label: "NEC on Prem" },
                            {
                              value: "Samsung on Prem",
                              label: "Samsung on Prem",
                            },
                            { value: "IPECS", label: "IPECS" },
                            {
                              value: "Callswitch Legacy",
                              label: "Callswitch Legacy",
                            },
                            {
                              value: "Callswitch One",
                              label: "Callswitch One",
                            },
                            { value: "Webex", label: "Webex" },
                            { value: "Luminate", label: "Luminate" },
                          ]}
                        />
                        <EditableField
                          label="Number of Licenses"
                          field="licenses"
                          value={phoneSystem.licenses}
                          type="number"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <FileEdit size={18} />
                        Notes
                      </h3>
                      <EditableField
                        label=""
                        field="notes"
                        value={phoneSystem.notes}
                        type="textarea"
                      />
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created By
                          </label>
                          <p
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} flex items-center gap-2`}
                          >
                            <User size={16} />
                            {phoneSystem.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(phoneSystem.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} flex items-center gap-2`}
                          >
                            <User size={16} />
                            {phoneSystem.lastModifiedByUser?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(phoneSystem.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === "activity" && (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Activity size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Activity tracking coming soon</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Quick Information
              </h3>
              <div className="space-y-3">
                {account && (
                  <button
                    onClick={() => navigate(`/accounts/${account.id}`)}
                    className={`w-full p-3 ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"} rounded-lg transition-colors text-left`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Building2 size={14} />
                      Account
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {account.name}
                    </div>
                  </button>
                )}

                {phoneSystem.systemType && (
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <PhoneCall size={14} />
                      System Type
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {phoneSystem.systemType}
                    </div>
                  </div>
                )}

                {phoneSystem.licenses && (
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Hash size={14} />
                      Licenses
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {phoneSystem.licenses}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Timeline
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Created
                  </span>
                  <span
                    className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {new Date(phoneSystem.createdAt).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Last Updated
                  </span>
                  <span
                    className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {new Date(phoneSystem.updatedAt).toLocaleDateString()}
                  </span>
                </div>

                {phoneSystem.creator && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Created By
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {phoneSystem.creator.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditPhoneSystemModal
          phoneSystem={phoneSystem}
          accountId={accountId}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchPhoneSystem();
          }}
        />
      )}
    </div>
  );
};

export default PhoneSystemDetail;
